import { customElement, state } from 'lit/decorators.js';
import { html } from 'lit';
import { connect } from 'pwa-helpers';
import store from '../../../store/store';
import { BaseComponent } from '../../base-component/base-component';
import { getQueryParam } from '../../../common/utils/util';
import { getWishListBySharingCodeData, getWishListBySharingCodeStatus } from '../store/wish-list.selector';
import { StoreState } from '../../../store/store.interface';
import { ActionStatus } from '../../../common/constants/action-status';
import {
  _renderLoadingTemplate,
  getSharedWishListUrl,
  getWishlistShareLinkOffCanvasTitle,
  renderNoWishListItemsTemplate,
  sharedWishlistIconButtons,
  WISH_LIST_SHARING_CODE_URL_PARAM,
} from '../utils/wish-list.util';
import { fetchWishListBySharingCode } from '../thunks/wish-list fetch-by-sharing-code.thunk';
import { renderErrorToast } from '../utils/wish-list-toast.util';
import { ErrorToastWishList } from '../constants/wish-list-error-toast-status';
import { WishList } from '../interfaces/wish-list-items.interface';
import { getShareLinkOffCanvasContent } from '../../off-canvas/common-off-canvases/utils/common-off-canvas.util';
import { getWishListFromEvent, openShareWishListOffCanvas } from '../utils/wish-list-offcanvas.util';
import { WishListDropdownConstants } from '../constants/wish-list-dropdown-constants';
import { WishListAnalytics } from '../utils/wish-list-analytics.util';
import { UserProfile } from '../../user-profile/user-profile.interface';
import { selectUser } from '../../user-profile/user-profile.selectors';

@customElement('mhecomm-shared-wish-list')
export class SharedWishList extends connect(store)(BaseComponent) {
  @state()
  wishListStaringCode = '';

  @state()
  loadingApiStatus = ActionStatus.idle;

  @state()
  wishList: WishList | null = null;

  @state()
  private user: UserProfile | undefined;

  /**
   * The analytics instance for the wish list.
   */
  private readonly wishListAnalytics: WishListAnalytics;

  constructor() {
    super();
    this.wishListAnalytics = new WishListAnalytics();
  }

  async connectedCallback() {
    super.connectedCallback();
    super.addTrackedEventListener(this, 'click', this.onWishlistOptionTrigger);
    this.wishListStaringCode = getQueryParam(WISH_LIST_SHARING_CODE_URL_PARAM) || '';
    store.dispatch(fetchWishListBySharingCode(this.wishListStaringCode));
  }

  async stateChanged(state: StoreState) {
    this.user = selectUser(state);
    this.loadingApiStatus = getWishListBySharingCodeStatus(state);
    if (this.loadingApiStatus === ActionStatus.succeeded) {
      this.wishList = getWishListBySharingCodeData(store.getState() as StoreState) as WishList;
    }
  }

  renderError() {
    return html`
      ${renderErrorToast({
        error: ErrorToastWishList.FETCHING_WISH_LIST_ITEMS,
        displayLoadingTemplate: true,
        autoHide: false,
        hideClose: true,
      })}
    `;
  }

  renderWishList(wishList: WishList) {
    return html`
      <mhecomm-wish-list-item-header
        .wishList=${wishList}
        .name="${wishList?.name ?? ''}"
        .allowedActions="${sharedWishlistIconButtons}"
      >
      </mhecomm-wish-list-item-header>
      ${wishList.wishlist_items?.length
        ? html`
            <mhecomm-paginated-wish-list-items
              .wishList="${wishList}"
              ?isReadOnly="${true}"
            ></mhecomm-paginated-wish-list-items>
          `
        : html` <div class="px-lg-3">
            ${renderNoWishListItemsTemplate({
              title: 'The list is empty!',
              message: 'The shared list does not contain any item.',
            })}
          </div>`}
    `;
  }

  renderMain() {
    if (!this.wishListStaringCode || this.loadingApiStatus === ActionStatus.failed) {
      return this.renderError();
    } else if (this.loadingApiStatus === ActionStatus.idle || this.loadingApiStatus === ActionStatus.loading) {
      return _renderLoadingTemplate();
    }
    if (!this.wishList) {
      return this.renderError();
    }
    return html`
      ${this.renderWishList(this.wishList)}
      <div>
        ${getShareLinkOffCanvasContent(
          getWishlistShareLinkOffCanvasTitle(this.wishList.name),
          getSharedWishListUrl(this.wishList.sharing_code),
          false,
          false,
          () => this.wishListAnalytics.shareWishList(
            this, 
            this.wishList?.wishlist_id!, 
            this.wishList?.name!, 
            getSharedWishListUrl(this.wishList?.sharing_code ?? ''), 
            this.user!
          )!
        )}
      </div>
    `;
  }

  renderComp() {
    return html`
      <div class="container my-4">
        ${this.renderMain()}
      </div>
    `;
  }

  /**
   * Handles the wish list option selected by the user.
   *
   * @param event
   */
  private onWishlistOptionTrigger(event: CustomEventInit): void {
    if (event?.detail?.eventName !== 'wish-list-option-selection') {
      return;
    }
    const wishList = getWishListFromEvent(event);
    if (!wishList) {
      console.error('Wish list not found, the action could not be performed.');
    }
    this.wishList = wishList!;
    if (event?.detail?.option === WishListDropdownConstants.SHARE) {
      openShareWishListOffCanvas(this.wishList!.wishlist_id);
      return;
    }
    console.error('Wish list action not supported.');
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-shared-wish-list': SharedWishList;
  }
}